import type { QuestionWithResponse } from '@amzn/gbsa-helios-typescript-client';
import type { MandatoryFailViolation } from '../../../shared/domain';
import { isQuestionCompleted } from './performAuditHelpers';

/**
 * Inputs the auto-advance readiness predicate reads. Every field is
 * required (no defaults) so callers are forced to think about each
 * gate — a missing "isSaving" flag silently defaulting to false is
 * exactly how auto-advance ships a race with an in-flight comment.
 */
export interface IsQuestionAutoAdvanceReadyInput {
  /**
   * The current (cache-overlayed) question row the perform surface
   * is showing. Auto-advance never fires when the question isn't
   * loaded — undefined is a hard `false`.
   */
  readonly question: QuestionWithResponse | undefined;
  /**
   * The close-audit violations for this question, as already
   * computed by `useCloseAuditGate.questionViolationsMap`. Undefined
   * or empty means the banner considers this question resolved.
   * We accept both `readonly MandatoryFailViolation[]` and
   * `undefined` so the caller can pass `map.get(id)` directly.
   */
  readonly violations: readonly MandatoryFailViolation[] | undefined;
  /**
   * True when the auditor is on the LAST question in the current
   * cursor order. The underlying `advance` action already no-ops
   * here, but returning `false` up front saves both a schedule and
   * the 500 ms wait.
   */
  readonly isLastQuestion: boolean;
  /** View-only surface (audit completed/expired) — never auto-advance. */
  readonly isReadOnly: boolean;
  /**
   * Audit-scoped save state from `useAuditResponseSaveState` (via
   * `useCloseAuditGate`). We never auto-advance while a write is in
   * flight because the readiness tuple would be reading against
   * partially-settled cache state — same guard the close button
   * uses to stay disabled.
   */
  readonly hasPendingResponseSaves: boolean;
  /**
   * True when at least one media upload is in progress. Uploads
   * settle independently of the mutation-save state above (their
   * S3 PUTs run outside the audit response coordinator), so this
   * needs its own signal.
   */
  readonly isMediaUploading: boolean;
}

/**
 * Predicate: "this question has no pending action items — advance
 * to the next one."
 *
 * Composes the same two sources the closure banner reads:
 *   1. `isQuestionCompleted` — parent answered AND every visible
 *      sub answered (scored subs by `responseResult`, data subs by
 *      `responseValue`), with the conditional-visibility and
 *      self-scoring-parent edge cases the banner already handles.
 *   2. `computeQuestionViolations` — no missing required comment /
 *      evidence / NA reason / data-sub value.
 *
 * Reads the OVERLAYED question so a just-persisted answer is
 * visible (`overlayFreshResponsesOntoQuestions` runs in
 * `PerformAuditPage`) and gates on the same audit-wide save state
 * `useCloseAuditGate` uses. Everything else (last-question, read-
 * only, media upload) is an explicit no-auto-advance rule the
 * caller passes in.
 *
 * PURE. No React, no side effects. The transition (false → true)
 * detection lives in {@link useAutoAdvanceOnComplete}.
 */
export function isQuestionAutoAdvanceReady(
  input: IsQuestionAutoAdvanceReadyInput,
): boolean {
  if (input.isReadOnly) return false;
  if (input.isLastQuestion) return false;
  if (input.hasPendingResponseSaves) return false;
  if (input.isMediaUploading) return false;
  if (!input.question) return false;
  if ((input.violations?.length ?? 0) > 0) return false;
  return isQuestionCompleted(input.question);
}
